/**
 * Cart Calculations Utility
 *
 * Centralized logic for calculating cart totals.
 * Used by OrderSummary, Checkout (PayPal), and any other component that needs totals.
 *
 * IMPORTANT: Any changes to calculation logic should be made HERE ONLY
 * to ensure consistency across the entire checkout flow.
 */

/**
 * Calculate the subtotal from cart
 * Uses volume_discount_total if available (handles quantity discounts from Vitals)
 *
 * @param {Object} cart - The cart object
 * @returns {number} Subtotal amount
 */
export const getSubtotal = (cart) => {
  if (!cart) return 0;

  return cart.volume_discount_total
    ? parseFloat(cart.volume_discount_total)
    : parseFloat(cart.subtotal || 0);
};

/**
 * Get the coupon/discount code amount
 *
 * @param {Object} cart - The cart object
 * @returns {number} Discount amount from coupon code
 */
export const getCouponDiscount = (cart) => {
  if (!cart?.discount_code_amount) return 0;
  return parseFloat(cart.discount_code_amount);
};

/**
 * Check if the applied coupon is a free shipping type
 *
 * @param {Object} cart - The cart object
 * @returns {boolean} True if free shipping coupon is applied
 */
export const isFreeShippingCoupon = (cart) => {
  return cart?.discount_code_type === 'free_shipping';
};

/**
 * Check if cart has any coupon applied
 *
 * @param {Object} cart - The cart object
 * @returns {boolean} True if a coupon is applied
 */
export const hasCouponApplied = (cart) => {
  if (!cart?.discount_code) return false;
  const codeDiscount = getCouponDiscount(cart);
  return codeDiscount > 0 || isFreeShippingCoupon(cart);
};

/**
 * Calculate shipping cost considering free shipping coupons
 *
 * @param {Object} cart - The cart object
 * @param {number} baseShippingCost - The base shipping cost before discounts
 * @returns {number} Final shipping cost
 */
export const getShippingCost = (cart, baseShippingCost) => {
  if (isFreeShippingCoupon(cart)) {
    return 0;
  }
  return parseFloat(baseShippingCost || 0);
};

/**
 * Calculate the complete cart total
 *
 * Formula: subtotal - couponDiscount + shipping + tip
 *
 * @param {Object} options
 * @param {Object} options.cart - The cart object
 * @param {number} options.shippingCost - Base shipping cost (before free shipping check)
 * @param {number} options.tip - Tip amount (default: 0)
 * @returns {number} Total amount
 */
export const calculateCartTotal = ({ cart, shippingCost = 0, tip = 0 }) => {
  const subtotal = getSubtotal(cart);
  const couponDiscount = getCouponDiscount(cart);
  const finalShipping = getShippingCost(cart, shippingCost);

  return subtotal - couponDiscount + finalShipping + (tip || 0);
};

/**
 * Calculate the complete cart total as a formatted string
 *
 * @param {Object} options - Same as calculateCartTotal
 * @returns {string} Total amount formatted to 2 decimal places
 */
export const calculateCartTotalFormatted = (options) => {
  return calculateCartTotal(options).toFixed(2);
};

/**
 * Get a breakdown of all cart amounts (useful for debugging/display)
 *
 * @param {Object} options
 * @param {Object} options.cart - The cart object
 * @param {number} options.shippingCost - Base shipping cost
 * @param {number} options.tip - Tip amount
 * @returns {Object} Breakdown of all amounts
 */
export const getCartBreakdown = ({ cart, shippingCost = 0, tip = 0 }) => {
  const subtotal = getSubtotal(cart);
  const couponDiscount = getCouponDiscount(cart);
  const finalShipping = getShippingCost(cart, shippingCost);
  const total = subtotal - couponDiscount + finalShipping + (tip || 0);

  return {
    subtotal,
    couponDiscount,
    couponCode: cart?.discount_code || null,
    isFreeShipping: isFreeShippingCoupon(cart),
    shippingCost: finalShipping,
    tip: tip || 0,
    total,
    totalFormatted: total.toFixed(2),
  };
};
